import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import * as authService from '@/app/services/authService';
import { getCurrentUserInfo, getUserPreferences } from '@/app/services/userService';
import { LoginCredentials, UserProfile } from '@/app/types/auth';
import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';

interface AuthState {
    token: string | null;
    user: UserProfile | null;
    userOrgId: number | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    loginAction: (credentials: LoginCredentials) => Promise<void>;
    logoutAction: () => void;
    setToken: (token: string | null) => void;
    fetchUserProfileAction: () => Promise<void>;
    getDefaultRoute: () => string;
}

// Use localStorage for storage
const storage = createJSONStorage(() => localStorage);

// Helper function to set token in the main process
const setTokenInMainProcess = async (token: string | null): Promise<void> => {
    if (!token) return;
    
    try {
        if (window.api?.db?.setAuthToken) {
            const result = await window.api.db.setAuthToken(token);
            console.log('Auth token set in main process:', result);
        }
    } catch (error) {
        console.error('Failed to set auth token in main process:', error);
    }
};

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            token: null,
            user: null,
            userOrgId: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            setToken: (token: string | null): void => {
                set({ token, isAuthenticated: !!token });
                
                // Also set token in the main process
                if (token) {
                    setTokenInMainProcess(token).catch(err => {
                        console.error('Error setting token in main process:', err);
                    });
                }
            },

            loginAction: async (credentials: LoginCredentials): Promise<void> => {
                set({ isLoading: true, error: null });
                try {
                    const response = await authService.login(credentials);
                    const token = response.access_token;
                    
                    // Set token first (but don't set isAuthenticated yet - wait for user profile)
                    set({
                        token: token,
                    });

                    // Store token in localStorage manually as well
                    localStorage.setItem('authToken', token);

                    // Set token in the main process for database operations
                    await setTokenInMainProcess(token);

                    // Fetch user profile after token is set
                    await get().fetchUserProfileAction();

                    // Set isAuthenticated after user profile is fetched
                    // This ensures getDefaultRoute() has access to user.role.name for correct routing
                    set({ isAuthenticated: true, isLoading: false });
                } catch (error: unknown) {
                    let errorMessage = 'Login failed';
                    if (axios.isAxiosError(error)) {
                        const axiosError = error as AxiosError<{ detail?: string }>;
                        errorMessage = axiosError.response?.data?.detail || axiosError.message || 'Login failed';
                    } else if (error instanceof Error) {
                        errorMessage = error.message;
                    }
                    
                    set({
                        error: errorMessage,
                        isLoading: false,
                        isAuthenticated: false,
                        token: null,
                        user: null,
                        userOrgId: null
                    });
                    // Clear any potentially persisted token on failure
                    localStorage.removeItem('authToken');
                }
            },

            fetchUserProfileAction: async (): Promise<void> => {
                try {
                    const userProfile = await getCurrentUserInfo();
                    // Default org from user profile
                    let orgId = userProfile?.organization_id || userProfile?.organization?.id || null;

                    // Fetch user preferences to get the active organization
                    try {
                        const prefs = await getUserPreferences();
                        if (prefs?.default_organization_id) {
                            orgId = prefs.default_organization_id;
                        }
                    } catch {
                        // Preferences may not exist yet, fall back to profile org
                    }

                    set({
                        user: userProfile,
                        userOrgId: orgId,
                        error: null
                    });

                    // If we have organization ID and token, we're ready for DB operations
                    if (orgId && get().token) {
                        // Here we could set organizationId in main process if needed
                        // For now we're just using the auth token
                    }
                } catch (error: unknown) {
                    let errorMessage = 'Failed to fetch user profile';
                    if (axios.isAxiosError(error)) {
                        const axiosError = error as AxiosError<{ detail?: string }>;
                        errorMessage = axiosError.response?.data?.detail || axiosError.message || 'Failed to fetch profile';
                    } else if (error instanceof Error) {
                        errorMessage = error.message;
                    }
                    console.error("Error fetching user profile:", errorMessage);
                    
                    // Set error but don't clear user or token
                    set({ error: errorMessage });
                }
            },

            logoutAction: (): void => {
                // Clear the token in the main process
                setTokenInMainProcess(null).catch(console.error);

                set({
                    token: null,
                    user: null,
                    userOrgId: null,
                    isAuthenticated: false,
                    isLoading: false,
                    error: null,
                });
                // Clear persisted token
                localStorage.removeItem('authToken');

                // Perform full page refresh to sync token states
                window.location.href = '/login';
            },

            /**
             * Get the default route based on user's role
             * Returns the appropriate dashboard/home route for the current user
             */
            getDefaultRoute: (): string => {
                const { user } = get();
                const roleName = user?.role?.name;

                // Route based on role
                switch (roleName) {
                    case 'client_manager':
                        return '/client-manager/dashboard';
                    case 'field_agent':
                        // Field agent routes (future implementation)
                        return '/dashboard'; // Fallback to default for now
                    case 'client':
                        // Client routes (future implementation)
                        return '/dashboard'; // Fallback to default for now
                    case 'superuser':
                    case 'org_admin':
                    case 'standard_user':
                    default:
                        return '/dashboard';
                }
            },
        }),
        {
            name: 'auth-storage', // Name for the persisted data key
            storage: storage,
            partialize: (state) => ({ 
                token: state.token, 
                isAuthenticated: state.isAuthenticated,
                user: state.user, // Also persist the user data
                userOrgId: state.userOrgId // Persist the organization ID
            }),
        }
    )
); 